import { get, post, del } from '@/lib/api-client';

// api-client 已经自带 /api/v1 前缀（baseURL config）；此处仅给业务 path
const BASE = '/ai-usage';

export interface SummaryResponse {
  totalTokens: number;
  totalCostUsd: string;
  activeDevices: number;
  activeProjects: number;
  activeUsers: number;
}

export interface TrendPoint {
  bucket: string;
  tokens: number;
  costUsd: string;
}

export interface TrendSeries {
  key: string;
  label?: string;
  points: TrendPoint[];
}

export interface BreakdownRow {
  key: string;
  tokens: number;
  costUsd: string;
  share: number;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TokenRow {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
  revokedAt: string | null;
  user?: { id: string; displayName: string; email: string };
}

export interface BlockRow {
  startedAt: string;
  endsAt: string;
  events: number;
  tokens: number;
  costUsd: string;
  models: string[];
  isActive: boolean;
  elapsedHours: number;
  remainingHours: number;
  burnRateUsdPerHour: string;
}

// v1.1 富 metadata 聚合响应类型
export interface ToolFreqItem {
  name: string;
  eventCount: number;
  useCount: number;
  share: number;
}
export interface SessionListItem {
  sessionId: string;
  userId?: string;
  userLabel?: string;
  projectBasename?: string;
  model?: string;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  turnCount: number;
  tokens: number;
  costUsd: string;
}
export interface SessionStatsResponse {
  sessionCount: number;
  avgDurationSec: number;
  avgTurns: number;
  durationBuckets: Record<string, number>;
  turnBuckets: Record<string, number>;
  recentSessions: SessionListItem[];
  pagination?: Pagination;
}
export interface SessionTurnsResponse {
  sessionId: string;
  summary: null | {
    userId: string;
    userLabel: string;
    projectBasename: string;
    projectPath: string;
    tool: string;
    startedAt: string;
    endedAt: string;
    durationSec: number;
    turnCount: number;
    totalTokens: number;
    totalCostUsd: string;
    toolUseAgg: Array<{ name: string; count: number }>;
  };
  turns: Array<{
    id: string;
    ts: string;
    turnIndex: number | null;
    model: string;
    inputTokens: number;
    outputTokens: number;
    cacheCreationTokens: number;
    cacheReadTokens: number;
    totalTokens: number;
    costUsd: string;
    toolUseCount: number | null;
    toolNames: string[] | null;
    stopReason: string | null;
    serviceTier: string | null;
    gitBranch: string | null;
    agentVersionEvent: string | null;
    worktreeLabel: string | null;
    cwdBasename: string | null;
  }>;
}
export interface DailyUserMatrixResponse {
  users: Array<{ id: string; label: string }>;
  dates: string[];
  cells: Array<{ date: string; userId: string; tokens: number; costUsd: string }>;
}
export interface TurnGapResponse {
  buckets: Record<string, number>;
  avgGapSec: number;
  medianGapSec: number;
}
export interface ServiceTierItem {
  tier: string;
  eventCount: number;
  tokens: number;
  costUsd: string;
  costShare: number;
}
export interface StopReasonItem {
  reason: string;
  eventCount: number;
  tokens: number;
  share: number;
}
export interface GitBranchItem {
  branch: string;
  eventCount: number;
  tokens: number;
  costUsd: string;
}

export interface DeviceRow {
  id: string;
  deviceId?: string;
  hostname: string;
  osUser: string | null;
  osPlatform: 'LINUX' | 'DARWIN' | 'WINDOWS';
  agentVersion: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
  user?: { id: string; displayName: string; email: string };
}

const qs = (params: Record<string, any>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export const aiUsageApi = {
  // ===== Admin =====
  adminSummary: (params: Record<string, any>) =>
    get<SummaryResponse>(`${BASE}/summary${qs(params)}`),
  adminTrend: (params: Record<string, any>) =>
    get<{ series: TrendSeries[] }>(`${BASE}/trend${qs(params)}`),
  adminBreakdown: (params: Record<string, any>) =>
    get<{ items: BreakdownRow[]; pagination: Pagination }>(`${BASE}/breakdown${qs(params)}`),

  // v1.1 富 metadata 聚合
  adminToolFrequency: (params: Record<string, any>) =>
    get<{ items: ToolFreqItem[] }>(`${BASE}/tool-frequency${qs(params)}`),
  adminSessionStats: (params: Record<string, any>) =>
    get<SessionStatsResponse>(`${BASE}/session-stats${qs(params)}`),
  adminSessionTurns: (sessionId: string) =>
    get<SessionTurnsResponse>(`${BASE}/sessions/${encodeURIComponent(sessionId)}/turns`),
  adminDailyUserMatrix: (params: Record<string, any>) =>
    get<DailyUserMatrixResponse>(`${BASE}/daily-user-matrix${qs(params)}`),
  adminTurnGap: (params: Record<string, any>) =>
    get<TurnGapResponse>(`${BASE}/turn-gap-distribution${qs(params)}`),
  adminServiceTierMix: (params: Record<string, any>) =>
    get<{ items: ServiceTierItem[] }>(`${BASE}/service-tier-mix${qs(params)}`),
  adminStopReasonMix: (params: Record<string, any>) =>
    get<{ items: StopReasonItem[] }>(`${BASE}/stop-reason-mix${qs(params)}`),
  adminGitBranchHeatmap: (params: Record<string, any>) =>
    get<{ items: GitBranchItem[] }>(`${BASE}/git-branch-heatmap${qs(params)}`),

  listDevices: (params: Record<string, any>) =>
    get<{ items: DeviceRow[]; pagination: Pagination }>(`${BASE}/devices${qs(params)}`),
  blockDevice: (id: string, reason: string) =>
    post<DeviceRow>(`${BASE}/devices/${id}/block`, { reason }),
  unblockDevice: (id: string, note?: string) =>
    post<DeviceRow>(`${BASE}/devices/${id}/unblock`, { note }),

  listTokens: (params: Record<string, any>) =>
    get<{ items: TokenRow[]; pagination: Pagination }>(`${BASE}/tokens${qs(params)}`),
  adminRevokeToken: (id: string, reason?: string) =>
    post<TokenRow>(`${BASE}/tokens/${id}/revoke`, { reason }),

  listDlq: (params: Record<string, any>) =>
    get<{ items: any[]; pagination: Pagination }>(`${BASE}/dlq${qs(params)}`),

  // ===== Me =====
  mySummary: (params: Record<string, any>) =>
    get<SummaryResponse>(`${BASE}/me/summary${qs(params)}`),
  myTrend: (params: Record<string, any>) =>
    get<{ series: TrendSeries[] }>(`${BASE}/me/trend${qs(params)}`),
  myBreakdown: (params: Record<string, any>) =>
    get<{ items: BreakdownRow[]; pagination: Pagination }>(`${BASE}/me/breakdown${qs(params)}`),
  myDevices: () => get<{ items: DeviceRow[] }>(`${BASE}/me/devices`),
  myBlocks: () => get<{ blocks: BlockRow[] }>(`${BASE}/me/blocks`),

  myTokens: () => get<{ items: TokenRow[] }>(`${BASE}/me/tokens`),
  createMyToken: (name: string) =>
    post<{ id: string; name: string; prefix: string; token: string; createdAt: string }>(
      `${BASE}/me/tokens`,
      { name },
    ),
  revokeMyToken: (id: string) => del(`${BASE}/me/tokens/${id}`),
};
